import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { RequireAuth, RequireRole, RequireSuperAdmin } from "./components/Guards";
import AppLayout from "./components/layout/AppLayout";
import { ROLES } from "./lib/roles";

import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Leads from "./pages/Leads";
import Dashboard from "./pages/Dashboard";
import Bitacora from "./pages/Bitacora";
import TapeUpload from "./pages/TapeUpload";
import Users from "./pages/Users";
import Fideicomisos from "./pages/Fideicomisos";
import FideicomisoNew from "./pages/FideicomisoNew";
import FideicomisoDetail from "./pages/FideicomisoDetail";
import ListasNegras from "./pages/ListasNegras";
import Tesoreria from "./pages/Tesoreria";
import Instrucciones from "./pages/Instrucciones";
import Patrimonio from "./pages/Patrimonio";
import ComiteTecnico from "./pages/ComiteTecnico";
import Contabilidad from "./pages/Contabilidad";
import Fiscal from "./pages/Fiscal";
import Honorarios from "./pages/Honorarios";
import Obligaciones from "./pages/Obligaciones";
import AdminTenants from "./pages/AdminTenants";
import AdminDashboardOperacion from "./pages/AdminDashboardOperacion";
import OperacionDiaria from "./pages/OperacionDiaria";
import Forbidden from "./pages/Forbidden";
import Placeholder from "./pages/Placeholder";

// V1 Garantía · Fase 5
import Facilities from "./pages/v1/Facilities";
import FacilityDetail from "./pages/v1/FacilityDetail";
import TapeCortes from "./pages/v1/TapeCortes";
import FacilityHub from "./pages/v1/FacilityHub";
import PortalExterno from "./pages/v1/PortalExterno";

// IA · Fase 6
import ColaHIL from "./pages/ia/ColaHIL";
import RevisionHIL from "./pages/ia/RevisionHIL";

// Fase 7 · A2 Conexión a la fuente
import Ingestas from "./pages/Ingestas";
import CredentialsVault from "./pages/CredentialsVault";

// Fase 12 · A6 Data Room + Analytics
import DataRoom from "./pages/DataRoom";

// Fase 14 · Gobierno Fiduciario
import GobiernoHub from "./pages/gobierno/GobiernoHub";
import Delegados from "./pages/gobierno/Delegados";
import MatrizAutorizacion from "./pages/gobierno/MatrizAutorizacion";
import CallBacks from "./pages/gobierno/CallBacks";
import ComiteMiembros from "./pages/gobierno/ComiteMiembros";
import ComiteSesiones from "./pages/gobierno/ComiteSesiones";
import Conflictos from "./pages/gobierno/Conflictos";
import ReporteConsejo from "./pages/gobierno/ReporteConsejo";

// Fase 15 · Aceptación G-02 (Pipeline + F-07/F-08/F-09)
import Pipeline from "./pages/aceptacion/Pipeline";
import SolicitudNueva from "./pages/aceptacion/SolicitudNueva";
import SolicitudDetalle from "./pages/aceptacion/SolicitudDetalle";
import Dictamenes from "./pages/aceptacion/Dictamenes";
import DictamenDetalle from "./pages/aceptacion/DictamenDetalle";
import ChecklistF08 from "./pages/aceptacion/ChecklistF08";
// Fase 16 · Honorarios + F-06
import TarifarioHonorarios from "./pages/honorarios/Tarifario";
import Cotizador from "./pages/honorarios/Cotizador";
import Cotizaciones from "./pages/honorarios/Cotizaciones";
import CotizacionDetalle from "./pages/honorarios/CotizacionDetalle";
// Fase 17 · Libro de Control Fiduciario (M-11..M-15 + IC)
import HubControl from "./pages/control/HubControl";
import { ConveniosList, ConvenioDetalle } from "./pages/control/Convenios";
import { SustitucionesList, SustitucionDetalle } from "./pages/control/Sustituciones";
import { TerminacionesList, TerminacionDetalle } from "./pages/control/Terminaciones";
import { PoderesList, PoderDetalle } from "./pages/control/Poderes";
import { RequerimientosList, RequerimientoDetalle } from "./pages/control/Requerimientos";
import { ContingenciaList, ContingenciaDetalle } from "./pages/control/Contingencia";
// Fase 18 · Refuerzos regulatorios finales
import DashboardDG from "./pages/direccion/Dashboard";
import EstadosCuenta from "./pages/regulatorios/EstadosCuenta";
import Nom151Verificador from "./pages/regulatorios/Nom151";
import ArchivoConcentracion from "./pages/regulatorios/ArchivoConcentracion";
import RetencionesLISR from "./pages/regulatorios/RetencionesLISR";
import BCP from "./pages/regulatorios/BCP";
import Inversiones from "./pages/regulatorios/Inversiones";

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        {/* Fase 11 · Landing pública (raíz sin auth) */}
                        <Route path="/" element={<Landing />} />
                        <Route path="/app" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/app/login" element={<Navigate to="/login" replace />} />

                        <Route path="/login" element={<Login />} />
                        <Route path="/403" element={<Forbidden />} />

                        <Route
                            element={
                                <RequireAuth>
                                    <AppLayout />
                                </RequireAuth>
                            }
                        >
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route
                                path="/leads"
                                element={
                                    <RequireRole roles={[ROLES.DIRECCION, ROLES.ADMIN_TI]}>
                                        <Leads />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/operacion-diaria"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.ADMINISTRADOR_FIDUCIARIO,
                                            ROLES.TESORERIA,
                                            ROLES.OPERACIONES,
                                            ROLES.ADMIN_TI,
                                            ROLES.DIRECCION,
                                            ROLES.AUDITOR,
                                            ROLES.RIESGOS,
                                            ROLES.FINANZAS,
                                        ]}
                                    >
                                        <OperacionDiaria />
                                    </RequireRole>
                                }
                            />
                            <Route path="/fideicomisos" element={<Fideicomisos />} />
                            <Route
                                path="/fideicomisos/nuevo"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.ADMINISTRADOR_FIDUCIARIO,
                                            ROLES.JURIDICO,
                                            ROLES.ADMIN_TI,
                                        ]}
                                    >
                                        <FideicomisoNew />
                                    </RequireRole>
                                }
                            />
                            <Route path="/fideicomisos/:id" element={<FideicomisoDetail />} />
                            <Route
                                path="/listas-negras"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.CUMPLIMIENTO,
                                            ROLES.ADMIN_TI,
                                            ROLES.AUDITOR,
                                            ROLES.DIRECCION,
                                        ]}
                                    >
                                        <ListasNegras />
                                    </RequireRole>
                                }
                            />

                            {/* Núcleo — placeholders Fase 1 */}
                            <Route
                                path="/partes-kyc"
                                element={<Placeholder title="Partes y KYC (vista global)" phase="Fase 1 · integrado en la ficha del fideicomiso" />}
                            />
                            <Route path="/patrimonio" element={<Patrimonio />} />
                            <Route path="/tesoreria" element={<Tesoreria />} />
                            <Route path="/instrucciones" element={<Instrucciones />} />
                            <Route path="/comite" element={<ComiteTecnico />} />
                            <Route
                                path="/contabilidad"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.FINANZAS,
                                            ROLES.ADMINISTRADOR_FIDUCIARIO,
                                            ROLES.DIRECCION,
                                            ROLES.AUDITOR,
                                            ROLES.ADMIN_TI,
                                        ]}
                                    >
                                        <Contabilidad />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/fiscal"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.FINANZAS,
                                            ROLES.ADMINISTRADOR_FIDUCIARIO,
                                            ROLES.DIRECCION,
                                            ROLES.AUDITOR,
                                            ROLES.ADMIN_TI,
                                        ]}
                                    >
                                        <Fiscal />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/honorarios"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.FINANZAS,
                                            ROLES.ADMINISTRADOR_FIDUCIARIO,
                                            ROLES.TESORERIA,
                                            ROLES.DIRECCION,
                                            ROLES.AUDITOR,
                                            ROLES.ADMIN_TI,
                                        ]}
                                    >
                                        <Honorarios />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/documental"
                                element={<Placeholder title="Documental" phase="Fase 2" />}
                            />
                            <Route path="/obligaciones" element={<Obligaciones />} />

                            {/* V1 Garantía — Fase 5 */}
                            <Route path="/v1/facilities" element={<Facilities />} />
                            <Route path="/v1/facilities/:id" element={<FacilityDetail />} />
                            <Route path="/v1/tape" element={<TapeUpload />} />
                            <Route path="/v1/tape/cortes" element={<TapeCortes />} />
                            <Route path="/v1/elegibilidad" element={<FacilityHub hub="elegibilidad" />} />
                            <Route path="/v1/borrowing-base" element={<FacilityHub hub="borrowing-base" />} />
                            <Route path="/v1/covenants" element={<FacilityHub hub="covenants" />} />
                            <Route path="/v1/waterfall" element={<FacilityHub hub="waterfall" />} />
                            <Route path="/v1/reportes" element={<FacilityHub hub="reportes" />} />
                            <Route
                                path="/v1/portal-externo"
                                element={
                                    <RequireRole roles={[ROLES.PARTE_EXTERNA, ROLES.ADMIN_TI, ROLES.AUDITOR, ROLES.DIRECCION]}>
                                        <PortalExterno />
                                    </RequireRole>
                                }
                            />

                            {/* Sistema */}
                            <Route
                                path="/ia/hil"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.CUMPLIMIENTO,
                                        ROLES.JURIDICO, ROLES.OPERACIONES, ROLES.DIRECCION,
                                        ROLES.AUDITOR, ROLES.ADMIN_TI, ROLES.RIESGOS,
                                        ROLES.FINANZAS, ROLES.TESORERIA,
                                    ]}>
                                        <ColaHIL />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/ia/hil/:id"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.CUMPLIMIENTO,
                                        ROLES.JURIDICO, ROLES.OPERACIONES, ROLES.DIRECCION,
                                        ROLES.AUDITOR, ROLES.ADMIN_TI, ROLES.RIESGOS,
                                        ROLES.FINANZAS, ROLES.TESORERIA,
                                    ]}>
                                        <RevisionHIL />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/bitacora"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.AUDITOR,
                                            ROLES.DIRECCION,
                                            ROLES.ADMIN_TI,
                                        ]}
                                    >
                                        <Bitacora />
                                    </RequireRole>
                                }
                            />

                            {/* Fase 7 · A2 Conexión a la fuente */}
                            <Route
                                path="/a2/ingestas"
                                element={
                                    <RequireRole
                                        roles={[
                                            ROLES.ADMINISTRADOR_FIDUCIARIO,
                                            ROLES.ADMIN_TI,
                                            ROLES.OPERACIONES,
                                            ROLES.RIESGOS,
                                            ROLES.DIRECCION,
                                            ROLES.AUDITOR,
                                            ROLES.FINANZAS,
                                        ]}
                                    >
                                        <Ingestas />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/a2/vault"
                                element={
                                    <RequireRole roles={[ROLES.ADMIN_TI]}>
                                        <CredentialsVault />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/data-room"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.JURIDICO,
                                        ROLES.ADMIN_TI, ROLES.AUDITOR, ROLES.DIRECCION,
                                        ROLES.CUMPLIMIENTO,
                                    ]}>
                                        <DataRoom />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/usuarios"
                                element={
                                    <RequireRole roles={[ROLES.ADMIN_TI]}>
                                        <Users />
                                    </RequireRole>
                                }
                            />

                            {/* Fase 14 · Gobierno Fiduciario */}
                            <Route
                                path="/gobierno"
                                element={
                                    <RequireRole roles={[
                                        ROLES.DIRECCION, ROLES.CONSEJO, ROLES.ADMIN_TI,
                                        ROLES.AUDITOR, ROLES.CUMPLIMIENTO, ROLES.JURIDICO,
                                        ROLES.DELEGADO_FIDUCIARIO,
                                    ]}>
                                        <GobiernoHub />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/gobierno/delegados"
                                element={
                                    <RequireRole roles={[
                                        ROLES.DIRECCION, ROLES.CONSEJO, ROLES.ADMIN_TI,
                                        ROLES.AUDITOR, ROLES.CUMPLIMIENTO, ROLES.JURIDICO,
                                    ]}>
                                        <Delegados />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/gobierno/matriz-autorizacion"
                                element={
                                    <RequireRole roles={[
                                        ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.AUDITOR,
                                        ROLES.CUMPLIMIENTO, ROLES.CONSEJO,
                                        ROLES.OPERACIONES, ROLES.TESORERIA,
                                        ROLES.DELEGADO_FIDUCIARIO,
                                    ]}>
                                        <MatrizAutorizacion />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/gobierno/call-backs"
                                element={
                                    <RequireRole roles={[
                                        ROLES.DELEGADO_FIDUCIARIO, ROLES.OPERACIONES,
                                        ROLES.ADMIN_TI, ROLES.DIRECCION, ROLES.AUDITOR,
                                    ]}>
                                        <CallBacks />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/gobierno/comite/miembros"
                                element={
                                    <RequireRole roles={[
                                        ROLES.CONSEJO, ROLES.DIRECCION, ROLES.ADMIN_TI,
                                        ROLES.AUDITOR, ROLES.CUMPLIMIENTO, ROLES.JURIDICO,
                                        ROLES.RIESGOS,
                                    ]}>
                                        <ComiteMiembros />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/gobierno/comite/sesiones"
                                element={
                                    <RequireRole roles={[
                                        ROLES.CONSEJO, ROLES.DIRECCION, ROLES.ADMIN_TI,
                                        ROLES.AUDITOR, ROLES.CUMPLIMIENTO, ROLES.JURIDICO,
                                        ROLES.RIESGOS,
                                    ]}>
                                        <ComiteSesiones />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/gobierno/conflictos"
                                element={<Conflictos />}
                            />
                            <Route
                                path="/gobierno/reporte-consejo"
                                element={
                                    <RequireRole roles={[
                                        ROLES.CONSEJO, ROLES.DIRECCION, ROLES.ADMIN_TI,
                                    ]}>
                                        <ReporteConsejo />
                                    </RequireRole>
                                }
                            />

                            {/* Operador · super_admin only */}
                            <Route
                                path="/admin/tenants"
                                element={
                                    <RequireSuperAdmin>
                                        <AdminTenants />
                                    </RequireSuperAdmin>
                                }
                            />
                            <Route
                                path="/admin/dashboard-operacion"
                                element={
                                    <RequireSuperAdmin>
                                        <AdminDashboardOperacion />
                                    </RequireSuperAdmin>
                                }
                            />

                            {/* Fase 15 · Aceptación G-02 */}
                            <Route
                                path="/aceptacion/pipeline"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.OPERACIONES,
                                        ROLES.CUMPLIMIENTO, ROLES.JURIDICO, ROLES.RIESGOS,
                                        ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.AUDITOR,
                                        ROLES.CONSEJO,
                                    ]}>
                                        <Pipeline />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/aceptacion/solicitudes/nueva"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.OPERACIONES,
                                        ROLES.CUMPLIMIENTO, ROLES.JURIDICO, ROLES.RIESGOS,
                                        ROLES.DIRECCION, ROLES.ADMIN_TI,
                                    ]}>
                                        <SolicitudNueva />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/aceptacion/solicitudes/:folio"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.OPERACIONES,
                                        ROLES.CUMPLIMIENTO, ROLES.JURIDICO, ROLES.RIESGOS,
                                        ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.AUDITOR,
                                        ROLES.CONSEJO,
                                    ]}>
                                        <SolicitudDetalle />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/aceptacion/dictamenes"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.OPERACIONES,
                                        ROLES.CUMPLIMIENTO, ROLES.JURIDICO, ROLES.RIESGOS,
                                        ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.AUDITOR,
                                        ROLES.CONSEJO,
                                    ]}>
                                        <Dictamenes />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/aceptacion/dictamenes/:folio"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.OPERACIONES,
                                        ROLES.CUMPLIMIENTO, ROLES.JURIDICO, ROLES.RIESGOS,
                                        ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.AUDITOR,
                                        ROLES.CONSEJO,
                                    ]}>
                                        <DictamenDetalle />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/expedientes/:fideicomiso_id/checklist-f08"
                                element={
                                    <RequireRole roles={[
                                        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.CUMPLIMIENTO,
                                        ROLES.JURIDICO, ROLES.DIRECCION, ROLES.ADMIN_TI,
                                        ROLES.OPERACIONES, ROLES.AUDITOR,
                                    ]}>
                                        <ChecklistF08 />
                                    </RequireRole>
                                }
                            />

                            {/* Fase 16 · Honorarios + F-06 */}
                            <Route
                                path="/honorarios/tarifario"
                                element={
                                    <RequireRole roles={[
                                        ROLES.FINANZAS, ROLES.DIRECCION, ROLES.ADMIN_TI,
                                    ]}>
                                        <TarifarioHonorarios />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/honorarios/cotizador"
                                element={
                                    <RequireRole roles={[
                                        ROLES.OPERACIONES, ROLES.FINANZAS, ROLES.DIRECCION,
                                        ROLES.ADMIN_TI, ROLES.CONSEJO,
                                    ]}>
                                        <Cotizador />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/honorarios/cotizaciones"
                                element={
                                    <RequireRole roles={[
                                        ROLES.OPERACIONES, ROLES.FINANZAS, ROLES.DIRECCION,
                                        ROLES.ADMIN_TI, ROLES.CONSEJO, ROLES.AUDITOR,
                                    ]}>
                                        <Cotizaciones />
                                    </RequireRole>
                                }
                            />
                            <Route
                                path="/honorarios/cotizaciones/:folio"
                                element={
                                    <RequireRole roles={[
                                        ROLES.OPERACIONES, ROLES.FINANZAS, ROLES.DIRECCION,
                                        ROLES.ADMIN_TI, ROLES.CONSEJO, ROLES.AUDITOR,
                                    ]}>
                                        <CotizacionDetalle />
                                    </RequireRole>
                                }
                            />

                            {/* Fase 17 · Libro de Control Fiduciario */}
                            {[
                                ["/control/hub", <HubControl />],
                                ["/control/convenios", <ConveniosList />],
                                ["/control/convenios/:id", <ConvenioDetalle />],
                                ["/control/sustituciones", <SustitucionesList />],
                                ["/control/sustituciones/:id", <SustitucionDetalle />],
                                ["/control/terminaciones", <TerminacionesList />],
                                ["/control/terminaciones/:id", <TerminacionDetalle />],
                                ["/control/poderes", <PoderesList />],
                                ["/control/poderes/:id", <PoderDetalle />],
                                ["/control/requerimientos", <RequerimientosList />],
                                ["/control/requerimientos/:id", <RequerimientoDetalle />],
                                ["/control/contingencia", <ContingenciaList />],
                                ["/control/contingencia/:id", <ContingenciaDetalle />],
                                // Fase 18 · Regulatorios finales (acceso amplio control-fiduciario)
                                ["/control/estados-cuenta", <EstadosCuenta />],
                                ["/control/nom151", <Nom151Verificador />],
                                ["/control/archivo-concentracion", <ArchivoConcentracion />],
                                ["/fiscal/retenciones-lisr", <RetencionesLISR />],
                                ["/control/bcp", <BCP />],
                                ["/control/inversiones", <Inversiones />],
                            ].map(([p, el]) => (
                                <Route
                                    key={p}
                                    path={p}
                                    element={
                                        <RequireRole roles={[
                                            ROLES.OPERACIONES, ROLES.JURIDICO, ROLES.CUMPLIMIENTO,
                                            ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.FINANZAS,
                                            ROLES.TESORERIA, ROLES.DIRECCION, ROLES.ADMIN_TI,
                                            ROLES.AUDITOR, ROLES.CONSEJO,
                                        ]}>{el}</RequireRole>
                                    }
                                />
                            ))}
                            {/* Fase 18 · Dashboard DG — acceso exclusivo DIRECCION/ADMIN_TI/CONSEJO */}
                            <Route
                                path="/direccion/dashboard"
                                element={
                                    <RequireRole roles={[
                                        ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.CONSEJO,
                                    ]}>
                                        <DashboardDG />
                                    </RequireRole>
                                }
                            />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
